// react
import { Suspense, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

// material ui
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  gridFilteredDescendantCountLookupSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// api
import {
  useGetEnergyGroupQuery,
  usePutEnergyGroupEnergyTreeNodeOptionsMutation,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// feature-components
import GoSharesDatagrid from '@/features/energyxplorer/components/esg/GoSharesDatagrid';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { showSnackbar } from '@/redux/snackbarSlice';
import { formatNumber } from '@/utils/formatters';

// relative
import { getTypeColor, getTypeIcon } from '../../../../utils/nodeTypeMapper';
import SeuDatagridToolbar from './SeuDatagridToolbar';
const CustomHeaderWithLabel = ({ label, props }) => {
  return (
    <Tooltip
      title={
        <>
          <Typography variant="body2">{props.colDef.headerName}</Typography>
          <Typography variant="body5">{props.colDef.description}</Typography>
        </>
      }
      placement="top"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="end"
        justifyContent="space-between"
        width="100%"
        height="100%"
      >
        <div
          style={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
            height: '100%',
          }}
        >
          <Typography
            variant="body2"
            sx={{ overflow: 'visible', whiteSpace: 'normal' }}
            fontWeight={'bold'}
          >
            {props.colDef.headerName}
          </Typography>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Typography
            variant="body5"
            fontSize={{ xs: 10, sm: 11, md: 12 }}
            color={'gray'}
          >
            {label}
          </Typography>
        </div>
      </Box>
    </Tooltip>
  );
};

function weightedAverage(data) {
  if (!data || data.length === 0) return 0;
  const [weightedSum, totalPower] = data.reduce(
    ([accW, accP], [CO2ePerUnit, power]) => [
      accW + CO2ePerUnit * power, // Σ(intensity · weight)
      accP + power, // Σ(weight)
    ],
    [0, 0],
  );

  return totalPower ? weightedSum / totalPower : 0;
}

const quarterKey = (year, month) => `Q${Math.ceil(month / 3)} ${year}`; // -> “Q3 2024”
const yearKey = (year) => String(year); // “2024” // <<< NEW

const SeuDatagrid = ({ data = [] }) => {
  const { energyGroupId, analysisType } = useParams({ strict: false });
  const localeText = useDatagridLocaleText();

  const { t } = useTranslation(`energyxplorer-esg-${analysisType || 'power'}`);
  const [showCO2, setShowCO2] = useState(true);
  const [showYears, setShowYears] = useState(false);

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );
  const isPortfolio = selectedEnergyGroup?.type?.includes('Portfolio');
  const [openGoShares, setOpenGoShares] = useState(false);

  const [selectedNode, setSelectedNode] = useState(null);
  const dispatch = useDispatch();
  const handleOpenGoShares = (node) => {
    setSelectedNode(node);
    setOpenGoShares(true);
  };
  const handleCloseGoShares = useCallback(() => {
    setOpenGoShares(false);
    setSelectedNode(null);
  }, []);
  const [putEnergyTreeNodeOptions, { isLoading: isUpdating }] =
    usePutEnergyGroupEnergyTreeNodeOptionsMutation();
  const handleSaveChanges = async (rows) => {
    const result = await putEnergyTreeNodeOptions({
      groupId: selectedEnergyGroup.energyGroupId,
      nodeId: selectedNode.id,
      putEnergyTreeNodeOptionsDto: {
        ...selectedNode.options,
        goShares: rows,
      },
    });

    if (!('error' in result)) {
      dispatch(showSnackbar({ message: 'Opdateret', severity: 'success' }));
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  const quarterDescription = useCallback(
    (headerName) => {
      const q = headerName.split(' ')[0];

      return (
        {
          Q1: t('seu_distribution_datagrid.columns.Q1'),
          Q2: t('seu_distribution_datagrid.columns.Q2'),
          Q3: t('seu_distribution_datagrid.columns.Q3'),
          Q4: t('seu_distribution_datagrid.columns.Q4'),
        }[q] ?? t('seu_distribution_datagrid.columns.Q_any')
      );
    },
    [t],
  );

  const metrics = useMemo(
    () => [
      {
        key: 'EnergyEligible',
        suffix: 'energy',
        label: t('consumption'),
        unit: t('consumption_unit'),
        decimals: 0,
      },
      {
        key: 'CO2e',
        suffix: 'co2e',
        label: t('emission'),
        unit: t('emission_unit'),
        decimals: 0,
      },
      {
        key: 'CO2ePerUnit',
        suffix: 'co2e_per_unit',
        label: t('decleration'),
        unit: t('decleration_unit'),
        decimals: 1,
      },
    ],
    [t],
  );
  /* ---------- 1a. discover all years & quarters ---------- */

  const years = useMemo(
    // <<< NEW
    () =>
      Array.from(new Set(data.flatMap((r) => r.Data.map((d) => d.year)))).sort(
        (a, b) => a - b,
      ),
    [data],
  );

  const quarters = useMemo(
    () =>
      Array.from(
        new Set(
          data.flatMap((r) => r.Data.map((d) => quarterKey(d.year, d.month))),
        ),
      ).sort((a, b) => {
        const [qa, ya] = a.split(' ');
        const [qb, yb] = b.split(' ');
        return ya !== yb ? +ya - +yb : +qa[1] - +qb[1];
      }),
    [data],
  );

  /* ---------- 2. build columns & column groups ---------- */
  const columns = useMemo(
    () =>
      [
        /* ----- (A) grand-total columns you already had ----- */
        ...metrics.map((m) => ({
          field: `total_${m.suffix}`,
          headerName: m.label,
          description: t(
            'seu_distribution_datagrid.columns.total_sum_all_years',
          ),
          type: 'number',
          width: 130,
          renderHeader: (p) => (
            <CustomHeaderWithLabel props={p} label={m.unit} />
          ),
          valueFormatter: (v) => formatNumber(v),
        })),

        /* ----- (B) NEW: per-year totals ----- */
        ...years.flatMap((y) =>
          metrics.map((m) => ({
            field: `${y}_${m.suffix}`, // 2024_energy
            headerName: m.label,
            description: t('seu_distribution_datagrid.columns.total_sum_year', {
              year: y,
            }),
            type: 'number',
            width: 130,
            renderHeader: (p) => (
              <CustomHeaderWithLabel props={p} label={m.unit} />
            ),
            valueFormatter: (v) => formatNumber(v),
          })),
        ),

        /* ----- (C) existing per-quarter columns ----- */
        ...quarters.flatMap((q) =>
          metrics.map((m) => ({
            field: `${q.replace(' ', '_')}_${m.suffix}`,
            headerName: m.label,
            description: quarterDescription(q),
            type: 'number',
            width: 130,
            renderHeader: (p) => (
              <CustomHeaderWithLabel props={p} label={m.unit} />
            ),
            valueFormatter: (v) => formatNumber(v),
          })),
        ),
        {
          field: 'goshare',
          headerName: t('seu_distribution_datagrid.columns.goshare'),
          description: t(
            'seu_distribution_datagrid.columns.goshare_description',
          ),
          type: 'number',
          width: 120,
          renderCell: (params) => {
            if (params.rowNode.parent === 'auto-generated-group-node-root')
              return null;

            return (
              <Button
                variant="outlined"
                onClick={() => handleOpenGoShares(params.row)}
              >
                {t('go_share')}
              </Button>
            );
          },
        },
      ]?.filter(
        isPortfolio && analysisType === 'power'
          ? () => true
          : (col) => col.field !== 'goshare',
      ),
    [
      metrics,
      years,
      quarters,
      t,
      isPortfolio,
      quarterDescription,
      analysisType,
    ],
  );

  const columnGroupingModel = useMemo(
    () => [
      {
        groupId: 'Total',
        headerName: t('total'),
        description: t('total_sum'),
        children: metrics.map((m) => ({ field: `total_${m.suffix}` })),
      },
      /* ----- (B) NEW: per-year group headers ----- */
      ...years.map((y) => ({
        groupId: y.toString(),
        headerName: y.toString(),
        description: t('seu_distribution_datagrid.columns.total_sum_year', {
          year: y,
        }),
        children: metrics.map((m) => ({ field: `${y}_${m.suffix}` })),
      })),
      /* ----- (C) existing per-quarter groups ----- */
      ...quarters.map((q) => ({
        groupId: q,
        headerName: q,
        description: quarterDescription(q),
        children: metrics.map((m) => ({
          field: `${q.replace(' ', '_')}_${m.suffix}`,
        })),
      })),
    ],
    [t, metrics, years, quarters, quarterDescription],
  );

  /* ---------- 3. build rows ---------- */
  const rows = () =>
    data.map((row, idx) => {
      // ① running sums for additive metrics
      const agg = { total_energy: 0, total_co2e: 0 };

      for (const d of row.Data) {
        const qBase = quarterKey(d.year, d.month).replace(' ', '_');
        const yBase = yearKey(d.year);

        metrics.forEach((m) => {
          // CO2ePerkWh is **derived**, not additive -> skip it here

          const qField = `${qBase}_${m.suffix}`;
          const yField = `${yBase}_${m.suffix}`;
          const qFieldCoefficient = `${qBase}_coefficient`;
          const yFieldCoefficient = `${yBase}_coefficient`;
          const val = d[m.key];

          if (m.key === 'CO2ePerUnit') {
            agg[qFieldCoefficient] = [
              ...(agg[qFieldCoefficient] ?? []),
              [d.CO2ePerUnit, d.EnergyEligible],
            ];
            agg[yFieldCoefficient] = [
              ...(agg[yFieldCoefficient] ?? []),
              [d.CO2ePerUnit, d.EnergyEligible],
            ];
            agg[`total_coefficient`] = [
              ...(agg['total_coefficient'] ?? []),
              [d.CO2ePerUnit, d.EnergyEligible],
            ];
            return;
          }

          /* per-quarter */
          agg[qField] = (agg[qField] ?? 0) + val;
          /* per-year */
          agg[yField] = (agg[yField] ?? 0) + val;
          /* grand total */
          agg[`total_${m.suffix}`] += val;
        });
      }
      // grand-total intensity
      agg.total_co2e_per_unit = weightedAverage(agg.total_coefficient);

      agg.total_co2e = agg.total_co2e / 1000; // convert to kg CO2e
      // per-year intensity
      years.forEach((y) => {
        agg[`${y}_co2e_per_unit`] = weightedAverage(agg[`${y}_coefficient`]);
        agg[`${y}_co2e`] = agg[`${y}_co2e`] / 1000; // convert to kg CO2e
      });

      // per-quarter intensity
      quarters.forEach((q) => {
        const key = q.replace(' ', '_');

        agg[`${key}_co2e_per_unit`] = weightedAverage(
          agg[`${key}_coefficient`],
        );
        agg[`${key}_co2e`] = agg[`${key}_co2e`] / 1000; // convert to kg CO2e
      });

      // ③ done
      const nodes = selectedEnergyGroup?.energyTreeNodes || [];
      const node = nodes.find((n) => n.energyTreeNodeId === row.Id);
      const name = node?.name || row.Name;
      const id = row.Id || idx;
      const type = node?.type || row.Name;
      const path = name === type ? [type] : [type, id];
      return {
        id: row.Id || idx,
        icon: type,
        name: name,
        type: type,
        path: path,
        options: nodes.find((n) => n.energyTreeNodeId === row.Id)?.options,
        ...agg,
      };
    })();

  const columnVisibilityModel = useMemo(() => {
    const visibleKeys = showCO2 ? ['CO2e', 'CO2ePerUnit'] : ['EnergyEligible'];

    const visibleTotalKeys = showCO2 ? ['CO2e'] : ['EnergyEligible'];

    const model = {};

    const isVisible = (metricKey) => visibleKeys.includes(metricKey);
    const isTotalsVisible = (metricKey) => visibleTotalKeys.includes(metricKey);

    // grand-totals
    metrics.forEach((m) => {
      model[`total_${m.suffix}`] = isTotalsVisible(m.key);
    });

    // yearly & quarterly
    years.forEach((y) =>
      metrics.forEach((m) => {
        model[`${y}_${m.suffix}`] = showYears ? isTotalsVisible(m.key) : false;
      }),
    );
    quarters.forEach((q) =>
      metrics.forEach((m) => {
        model[`${q.replace(' ', '_')}_${m.suffix}`] = showYears
          ? false
          : isVisible(m.key);
      }),
    );

    return model;
  }, [showCO2, metrics, years, quarters, showYears]);

  /* ---------- 4. render ---------- */
  const getTreeDataPath = (row) => row.path;

  function CustomGridTreeDataGroupingCell(props) {
    const { id, field, rowNode, row } = props;
    const apiRef = useGridApiContext();
    const filteredDescendantCountLookup = useGridSelector(
      apiRef,
      gridFilteredDescendantCountLookupSelector,
    );
    const filteredDescendantCount =
      filteredDescendantCountLookup[rowNode.id] ?? 0;

    const handleClick = (event) => {
      if (rowNode.type !== 'group') {
        return;
      }

      apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
      apiRef.current.setCellFocus(id, field);
      event.stopPropagation();
    };

    return (
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        height="100%"
        sx={{ pl: rowNode.depth * 2 }}
        gap={1}
        width="100%"
      >
        <Avatar
          variant="rounded"
          size="small"
          sx={{
            backgroundColor: getTypeColor(row.type) || '#555',
            color: '#fff',
            borderRadius: 2,
            flexShrink: 0,
          }}
        >
          <i className={`fas ${getTypeIcon(row.type)}`}></i>
        </Avatar>
        <Typography
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.name}
        </Typography>
        {filteredDescendantCount > 0 && (
          <IconButton
            onClick={handleClick}
            tabIndex={-1}
            size="small"
            sx={{ flexShrink: 0, ml: 'auto' }}
          >
            {rowNode.childrenExpanded ? (
              <ExpandMoreRoundedIcon />
            ) : (
              <ChevronRightRoundedIcon />
            )}
          </IconButton>
        )}
      </Box>
    );
  }
  const groupingColDef = {
    headerName: t('seu_distribution_datagrid.columns.name'),
    renderCell: (params) => <CustomGridTreeDataGroupingCell {...params} />,
    minWidth: 250, // or your preferred width
  };

  return (
    <Box sx={{ width: '100%' }}>
      <DataGridPremium
        showToolbar
        treeData
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        columnVisibilityModel={columnVisibilityModel}
        isGroupExpandedByDefault={() => true}
        hideFooter
        sx={{
          '& .MuiDataGrid-main': {
            borderTop: 'none',
            borderTopLeftRadius: '0 !important',
            borderTopRightRadius: '0 !important',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderTopLeftRadius: '0 !important',
            borderTopRightRadius: '0 !important',
          },
          '& .MuiDataGrid-root': {
            borderTopLeftRadius: '0 !important',
            borderTopRightRadius: '0 !important',
          },
        }}
        pinnedRows={{
          top:
            rows.length > 0
              ? [rows.find((o) => o.name === 'Hovedmåler')].filter(Boolean)
              : [],
        }}
        pinnedColumns={{
          right: selectedEnergyGroup.type.includes('Portfolio')
            ? ['goshare']
            : [],
        }}
        localeText={localeText}
        slots={{
          toolbar: SeuDatagridToolbar,
        }}
        slotProps={{
          toolbar: {
            setShowCO2: setShowCO2,
            setShowYears: setShowYears,
            showCO2: showCO2,
            showYears: showYears,
          },
        }}
      />
      <Suspense fallback={<div>{t('loading')}</div>}>
        <GoSharesDatagrid
          handleSaveChanges={handleSaveChanges}
          initialRows={
            selectedNode?.options?.goShares
              ? selectedNode.options.goShares.map((share) => ({
                  percentage: share.value,
                  timestamp: share.start_time,
                  id: share.go_share_id,
                }))
              : []
          }
          sx={{}}
          nodeId={selectedNode?.id}
          onClose={handleCloseGoShares}
          open={openGoShares}
          enableOptimisticEdit={false}
          isLoading={isUpdating}
        />
      </Suspense>
    </Box>
  );
};

export default SeuDatagrid;
